import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Shield } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { BRAND } from '@/data/brand';

export default function PrivacyPolicy() {
  const { lang } = useLang();
  const isEs = lang === 'es';

  return (
    <div className="min-h-screen bg-[#FFFBFB] text-[#1F1A24]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#574A66] hover:text-[#FF7597] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {isEs ? 'Volver al inicio' : 'Back to home'}
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <span className="h-12 w-12 rounded-full bg-[#FAEFC2] inline-flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#A99BE0]" />
          </span>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A99BE0] font-bold">
            {isEs ? 'POLÍTICA DE TRATAMIENTO DE DATOS' : 'PRIVACY POLICY'}
          </p>
        </div>

        <h1 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tighter text-[#1F1A24]">
          {isEs ? 'Política de Privacidad' : 'Privacy Policy'}
        </h1>
        <p className="mt-2 text-sm text-[#574A66]">
          {isEs
            ? `Jelly by Enkor · Última actualización: ${new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}`
            : `Jelly by Enkor · Last updated: ${new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })}`}
        </p>

        {isEs ? <PolicyES /> : <PolicyEN />}

        <div className="mt-16 p-6 rounded-3xl bg-[#FAEFC2]/40 border border-[#F4DD8C]/40">
          <p className="text-sm font-bold text-[#1F1A24]">{isEs ? '¿Tienes preguntas o quieres eliminar tus datos?' : 'Questions or want to delete your data?'}</p>
          <p className="mt-2 text-sm text-[#574A66]">
            {isEs ? 'Escríbenos a' : 'Email us at'}{' '}
            <a href={`mailto:${BRAND.email}`} className="font-bold text-[#FF7597] underline">{BRAND.email}</a>
            {isEs ? ' o por WhatsApp al ' : ' or WhatsApp us at '}
            <a
              href={`https://wa.me/${(BRAND.whatsapp || '').replace(/\D/g, '')}`}
              className="font-bold text-[#FF7597] underline"
              target="_blank" rel="noopener noreferrer"
            >{BRAND.whatsapp}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl md:text-2xl font-black tracking-tighter text-[#A99BE0]">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[#574A66]">{children}</div>
  </section>
);

const PolicyES = () => (
  <>
    <Section title="1. Responsable del tratamiento">
      <p>
        <strong>Jelly by Enkor</strong> (en adelante "Jelly") es responsable del tratamiento de tus datos personales conforme a la <strong>Ley 1581 de 2012</strong> (Ley de Habeas Data Colombia) y su Decreto Reglamentario 1377 de 2013.
      </p>
      <p>
        <strong>Contacto:</strong> {BRAND.email} · {BRAND.whatsapp}
      </p>
    </Section>

    <Section title="2. Datos que recolectamos">
      <p>Al registrarte en nuestro sitio o eventos (como Expobelleza 2026), recolectamos:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Nombre completo</li>
        <li>Número de celular (WhatsApp)</li>
        <li>Correo electrónico</li>
        <li>Ciudad de residencia</li>
        <li>Información comercial: tipo de cliente (minorista/mayorista), interés principal, producto favorito</li>
        <li>Datos del evento donde te inscribiste</li>
      </ul>
    </Section>

    <Section title="3. Finalidad del tratamiento">
      <p>Usamos tus datos para:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Participar en sorteos y promociones de Jelly</li>
        <li>Contactarte por WhatsApp o correo para anunciar ganadores y entregar premios</li>
        <li>Enviarte información comercial sobre productos, lanzamientos, promociones y eventos</li>
        <li>Mejorar nuestra oferta de productos según tus preferencias</li>
        <li>Identificar potenciales distribuidores o mayoristas</li>
        <li>Análisis estadístico interno (siempre de manera agregada y anónima)</li>
      </ul>
    </Section>

    <Section title="4. Tus derechos como titular">
      <p>Como titular de los datos, tienes derecho a:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li><strong>Conocer</strong> qué datos tenemos tuyos</li>
        <li><strong>Actualizar</strong> o corregir tus datos en cualquier momento</li>
        <li><strong>Rectificar</strong> información inexacta o incompleta</li>
        <li><strong>Solicitar la supresión</strong> ("derecho al olvido") de tus datos</li>
        <li><strong>Revocar</strong> en cualquier momento tu autorización</li>
        <li><strong>Presentar quejas</strong> ante la Superintendencia de Industria y Comercio (SIC)</li>
      </ul>
      <p>Para ejercer estos derechos, escríbenos a <strong>{BRAND.email}</strong>. Te responderemos en máximo 10 días hábiles.</p>
    </Section>

    <Section title="5. Confidencialidad y seguridad">
      <p>
        Tus datos están protegidos con medidas técnicas, administrativas y físicas para evitar acceso no autorizado, pérdida o alteración. Nuestros sistemas usan cifrado TLS y autenticación segura.
      </p>
      <p>
        <strong>No vendemos ni compartimos</strong> tus datos con terceros para fines comerciales ajenos a Jelly. Únicamente compartimos información con proveedores técnicos (hosting, envío de correo) bajo contratos de confidencialidad.
      </p>
    </Section>

    <Section title="6. Tiempo de conservación">
      <p>Conservamos tus datos mientras tengas relación activa con Jelly o mientras sean necesarios para los fines descritos. Puedes pedir su eliminación cuando quieras escribiéndonos.</p>
    </Section>

    <Section title="7. Comunicaciones comerciales">
      <p>
        Al aceptar esta política, autorizas a Jelly a enviarte información comercial por <strong>WhatsApp, correo electrónico y SMS</strong>. Puedes darte de baja de estas comunicaciones en cualquier momento usando el enlace de "darse de baja" en cada correo, o escribiendo <em>BAJA</em> por WhatsApp.
      </p>
    </Section>

    <Section title="8. Cookies y tecnologías similares">
      <p>
        Nuestro sitio usa cookies técnicas para funcionamiento básico (idioma, sesión de carrito). No usamos cookies de seguimiento publicitario de terceros.
      </p>
    </Section>

    <Section title="9. Modificaciones">
      <p>
        Podemos actualizar esta política. Notificaremos cambios importantes por correo o WhatsApp. La fecha de "última actualización" en la parte superior siempre refleja la versión vigente.
      </p>
    </Section>
  </>
);

const PolicyEN = () => (
  <>
    <Section title="1. Data Controller">
      <p>
        <strong>Jelly by Enkor</strong> ("Jelly") is responsible for processing your personal data in accordance with applicable data protection laws in Colombia (Ley 1581/2012).
      </p>
      <p>
        <strong>Contact:</strong> {BRAND.email} · {BRAND.whatsapp}
      </p>
    </Section>

    <Section title="2. Data we collect">
      <p>When you register on our site or events:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Full name</li>
        <li>Phone number (WhatsApp)</li>
        <li>Email address</li>
        <li>City of residence</li>
        <li>Commercial info: customer type, main interest, favorite product</li>
      </ul>
    </Section>

    <Section title="3. Purposes">
      <ul className="list-disc ml-6 space-y-1">
        <li>Run raffles and promotions</li>
        <li>Contact you by WhatsApp/email to announce winners</li>
        <li>Send commercial info about products, launches and events</li>
        <li>Improve our offering based on your preferences</li>
        <li>Identify potential distributors</li>
      </ul>
    </Section>

    <Section title="4. Your rights">
      <ul className="list-disc ml-6 space-y-1">
        <li>Know which data we have about you</li>
        <li>Update or correct your data</li>
        <li>Request deletion of your data</li>
        <li>Revoke your authorization at any time</li>
      </ul>
      <p>Contact us at <strong>{BRAND.email}</strong>.</p>
    </Section>

    <Section title="5. Security">
      <p>Your data is protected with technical and organizational measures (TLS encryption, secure auth). We do not sell or share your data with third parties for unrelated commercial purposes.</p>
    </Section>

    <Section title="6. Commercial communications">
      <p>By accepting, you authorize Jelly to send commercial info via WhatsApp, email and SMS. You can opt out at any time.</p>
    </Section>
  </>
);
